import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './OnboardingTour.css';

// Aanwijs-rondleiding over de echte UI. Twee soorten stappen:
// - menu-stap  (navId):  licht een Sidebar-knop uit
// - pagina-stap (route): navigeert éérst naar die pagina en wijst daar de
//   actieknop aan ([data-tour="<selector-id>"]) — zo ziet de docent niet
//   alleen wáár iets zit, maar ook hoe je het gebruikt.
// De voortgang staat in sessionStorage, want elke navigatie her-mount de
// wizard (ProtectedRoute per route); de tour pakt dan verder waar hij was.
// Stappen van features die uit staan vallen automatisch weg (menu-anker
// ontbreekt dan in de DOM).
export const TOUR_KEY = 'tw-tour-stap';

const TOUR_STAPPEN = [
  { navId: 'dashboard', titel: 'Dashboard', tekst: 'Je startpunt: wat er nu live staat, wat je aandacht nodig heeft (nakijken, meldingen) en je laatste resultaten.' },
  { navId: 'communicatie', titel: 'Communicatie', tekst: 'Oefensessies met vragen voor je klas, zonder vergrendeling. Leerlingen doen mee in de browser en zien daarna hun resultaat. We laten even zien hoe dat werkt.' },
  { vereistNav: 'communicatie', route: '/communicatie-page', selector: 'nieuwe-communicatie', titel: 'Zo maak je een communicatiesessie', tekst: 'Druk hier op "+ Nieuwe sessie": maak zelf vragen, laat ze met AI maken of haal ze uit een hoofdstuk van je boek. Koppel een klas en zet de sessie live. Leerlingen doen mee met de code of vinden de sessie onder hun vak. Open vragen worden automatisch nagekeken; jij keurt goed.' },
  { navId: 'toetsen', titel: 'Toetsen', tekst: 'Hier maak en beheer je je toetsen.' },
  { vereistNav: 'toetsen', route: '/toetsen-page', selector: 'nieuwe-toets', titel: 'Zo maak je een toets', tekst: 'Druk hier op "+ Nieuwe toets": zelf vragen maken, met AI genereren, of rechtstreeks uit een hoofdstuk van je boek.' },
  { navId: 'resultaten', titel: 'Analyse', tekst: 'Resultaten en analyses per toets of sessie en per leerling, zodra er is ingeleverd.' },
  { navId: 'leerlingen', titel: 'Mijn Leerlingen', tekst: 'Alle leerlingen aan wie je lesgeeft, met hun voortgang.' },
  { navId: 'leerdoelen', titel: 'Leerdoelen', tekst: 'Leerdoelen bekijken en koppelen aan je toetsen.' },
  { navId: 'vragenbank', titel: 'Vragenbank', tekst: 'Bewaar hier vragen om ze in meerdere toetsen te hergebruiken.' },
  { navId: 'boekenplank', titel: 'Boekenplank', tekst: 'De boeken van je school, per hoofdstuk en paragraaf. Hieruit kun je vragen laten maken voor een sessie of toets.' },
  { navId: 'lesmodus', titel: 'Lesmodus', tekst: 'Lesmateriaal klaarzetten waar leerlingen vergrendeld in werken. Even kijken hoe dat gaat.' },
  { vereistNav: 'lesmodus', route: '/lesmodus', selector: 'nieuwe-les', titel: 'Zo werkt Lesmodus', tekst: 'Druk hier op "+ Nieuwe les": voeg PDF\'s, PowerPoints of links toe en vink een klas aan, anders ziet niemand de les. Leerlingen openen \'m met een startcode en zitten er vergrendeld in tot de les stopt.' },
  { navId: 'klassen', titel: 'Klassen', tekst: 'Je klassen en de leerlingen daarin. Ook dit wijzen we even aan.' },
  { vereistNav: 'klassen', route: '/klassen-page', selector: 'nieuwe-klas', titel: 'Zo maak je een klas', tekst: 'Klassen die je school heeft aangeleverd staan hier al; leerlingen komen er vanzelf in zodra ze één keer via Entree inloggen. Zelf een klas maken kan met "+ Nieuwe klas".' },
  { navId: 'agenda', titel: 'Agenda', tekst: 'Je weekagenda met geplande toetsen, lessen en afspraken.' },
  { navId: 'coaching', titel: 'Coaching', tekst: 'Coachgesprekken plannen en vastleggen.' },
  { navId: 'instellingen', titel: 'Instellingen', tekst: 'Hier pas je later je vakken, voorkeuren en koppelingen aan. Veel plezier!' },
];

const leesIndex = () => {
  try { return parseInt(sessionStorage.getItem(TOUR_KEY) || '0', 10) || 0; } catch { return 0; }
};

function OnboardingTour({ onFinish }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Filter op het menu-anker: dat staat op élke docentpagina, dus de
  // stappenlijst is stabiel, ook midden in de tour na een navigatie.
  const stappen = useMemo(
    () => TOUR_STAPPEN.filter(s => document.querySelector(`[data-tour="${s.navId || s.vereistNav}"]`)),
    []
  );
  const [i, setI] = useState(() => Math.min(leesIndex(), Math.max(0, stappen.length - 1)));
  const [rect, setRect] = useState(null);
  const zoekTimer = useRef(null);

  const gaNaar = useCallback((n) => {
    try { sessionStorage.setItem(TOUR_KEY, String(n)); } catch {}
    setRect(null);
    setI(n);
  }, []);

  const stap = stappen[i];
  const laatste = i === stappen.length - 1;

  // Element van de huidige stap zoeken; pagina-stappen navigeren eerst en
  // wachten (pollend) tot de pagina zijn actieknop heeft gerenderd.
  useEffect(() => {
    if (!stap) return undefined;
    if (stap.route && location.pathname !== stap.route) {
      navigate(stap.route);
      return undefined; // her-mount pakt de tour hier weer op
    }
    const doelSelector = `[data-tour="${stap.selector || stap.navId}"]`;
    let pogingen = 0;
    const zoek = () => {
      const el = document.querySelector(doelSelector);
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        return;
      }
      pogingen += 1;
      if (pogingen > 25) {
        // Element komt niet (lege staat of gewijzigde pagina): stap overslaan.
        if (i < stappen.length - 1) gaNaar(i + 1);
        else onFinish();
        return;
      }
      zoekTimer.current = setTimeout(zoek, 240);
    };
    zoek();
    const opnieuw = () => zoek();
    window.addEventListener('resize', opnieuw);
    return () => {
      clearTimeout(zoekTimer.current);
      window.removeEventListener('resize', opnieuw);
    };
  }, [stap, i, stappen.length, location.pathname, navigate, gaNaar, onFinish]);

  useEffect(() => {
    if (stappen.length === 0) onFinish();
  }, [stappen, onFinish]);

  if (stappen.length === 0 || !stap || !rect) return null;

  const pad = 6;
  const rechts = rect.left + rect.width / 2 < window.innerWidth / 2;
  const tooltipStijl = {
    top: Math.max(12, Math.min(rect.top - 8, window.innerHeight - 260)),
    ...(rechts
      ? { left: Math.min(rect.left + rect.width + 18, window.innerWidth - 360) }
      : { right: Math.max(12, window.innerWidth - rect.left + 18) }),
  };

  return (
    <div className="obt-overlay">
      <div
        className="obt-spotlight"
        style={{
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
        }}
      />
      <div className="obt-tooltip" style={tooltipStijl}>
        <div className="obt-teller">Stap {i + 1} van {stappen.length}</div>
        <div className="obt-titel">{stap.titel}</div>
        <div className="obt-tekst">{stap.tekst}</div>
        <div className="obt-voortgang">
          <div className="obt-voortgang-balk" style={{ width: `${((i + 1) / stappen.length) * 100}%` }} />
        </div>
        <div className="obt-knoppen">
          <button className="obt-skip" onClick={onFinish}>Rondleiding overslaan</button>
          <div className="obt-knoppen-rechts">
            {i > 0 && (
              <button className="obt-vorige" onClick={() => gaNaar(i - 1)}>Vorige</button>
            )}
            <button className="obt-volgende" onClick={() => (laatste ? onFinish() : gaNaar(i + 1))}>
              {laatste ? 'Aan de slag' : 'Volgende'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingTour;
